import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GamesList from '@/components/GamesList';
import Header from '@/components/Header';
import PointsFilter from '@/components/PointsFilter';
import CompetitionCheckboxes from '@/components/CompetitionCheckboxes';
import Footer from '@/components/Footer';
import MetaGenerator from '@/components/seo/MetaGenerator';
import StructuredDataManager from '@/components/seo/StructuredDataManager';
import BreadcrumbNav from '@/components/seo/BreadcrumbNav';
import FAQSection from '@/components/seo/FAQSection';
import SportEmoji from '@/components/SportEmojis';
import SoccerViewSwitcher from '@/components/SoccerViewSwitcher';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';
import { fetchGamesData, filterGames, sportLimits, sportDefaultValues, clearGamesCache } from '@/api/gamesService';
import { FilterOptions, SportType, SoccerCompetition } from '@/types';
import { getSportFaqs } from '@/config/faqs';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { sportDescriptions, getSportDisplayName, getCompetitionDisplayName, getAvailableSoccerCompetitions } from '@/utils/sportUtils';
import { useWebVitals } from '@/hooks/useWebVitals';
import RelatedSportsNav from '@/components/RelatedSportsNav';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import {
  AnalyticsEntrySource,
  buildFilterChangedEvent,
  getCountBucket,
  getEntrySource,
  getUtmParams,
  getVisibleGames,
  trackAnalyticsEvent,
} from '@/lib/analytics';
import { targetSeoMetadata } from '@/config/seoMetadata.js';

interface SportPageProps {
  sport: SportType;
}

const SportPage: React.FC<SportPageProps> = ({ sport }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { competition } = useParams<{ competition?: string }>();
  const [showAllScores, setShowAllScores] = useState(false);
  const pageStartKeyRef = useRef<string | null>(null);
  const { consentSettings } = useCookieConsent();
  
  // Initialize Web Vitals monitoring
  useWebVitals();
  
  // Create initial filter options
  const initialFilters: FilterOptions = {
    sportType: sport,
    maxValue: sportDefaultValues[sport].default,
    minValue: undefined,
    competitions: []
  };

  // Use URL filters hook
  const { filters, updateFilters } = useUrlFilters(initialFilters);
  
  // Get display name for sport and competition
  const sportDisplayName = getSportDisplayName(sport);
  const competitionDisplayName = competition ? getCompetitionDisplayName(sport, competition) : null;

  // Configure React Query with better cache management
  const { data: gamesData, isLoading, error } = useQuery({
    queryKey: ['games'],
    queryFn: fetchGamesData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearGamesCache();
    };
  }, []);

  // Redirect if the current sportType doesn't match the route
  useEffect(() => {
    if (filters.sportType !== sport) {
      updateFilters({ 
        sportType: sport,
        maxValue: sportDefaultValues[sport].default,
        minValue: undefined,
        competitions: []
      });
    }
  }, [filters.sportType, sport, updateFilters]);

  // Get current sport in proper format for structured data
  const getCurrentSport = (): 'NBA' | 'NFL' | 'MLB' | 'NHL' | 'Soccer' => {
    if (sport === 'soccer') return 'Soccer';
    return sport.toUpperCase() as 'NBA' | 'NFL' | 'MLB' | 'NHL';
  };

  // Get available competitions from URL param or filters
  const getActiveCompetitions = (): string[] => {
    if (competitionDisplayName) {
      return [competitionDisplayName];
    }
    return filters.competitions || [];
  };

  const handleMaxValueChange = (value: number) => {
    updateFilters({ maxValue: value });
  };

  const handleMinValueChange = (value: number) => {
    updateFilters({ minValue: value });
  };

  const handleCompetitionsChange = (competitions: SoccerCompetition[]) => {
    updateFilters({ competitions });
  };

  const getDefaultMaxValue = (sport: SportType): number => sportDefaultValues[sport].default;

  const getDefaultMinValue = (sport: SportType): number => sportLimits[sport].min;

  const toggleAllScores = () => setShowAllScores(!showAllScores);

  const filteredGames = gamesData?.games ? filterGames(gamesData.games, filters) : [];
  const visibleGames = useMemo(() => getVisibleGames(filteredGames), [filteredGames]);
  const sourceSportGameCount = gamesData?.games?.[sport] ? Object.keys(gamesData.games[sport]).length : undefined;
  const hasNoSourceGames = !isLoading && !error && sourceSportGameCount === 0;

  // Convert games to SEO-friendly format
  const seoGames = filteredGames.map(game => ({
    id: game.id,
    sport: getCurrentSport(),
    homeTeam: game.homeTeam.name,
    awayTeam: game.awayTeam.name,
    date: game.date,
    competition: game.competition,
    homeScore: game.homeScore,
    awayScore: game.awayScore
  }));

  // Get the sport-specific colors for styling
  const getToggleColorClass = () => {
    switch (sport) {
      case 'soccer':
        return 'bg-green-700 hover:bg-green-600';
      case 'nba':
        return 'bg-orange-600 hover:bg-orange-500';
      case 'nfl':
        return 'bg-amber-700 hover:bg-amber-600';
      case 'mlb':
        return 'bg-blue-700 hover:bg-blue-600';
      case 'nhl':
        return 'bg-cyan-700 hover:bg-cyan-600';
      default:
        return 'bg-primary hover:bg-primary/90';
    }
  };

  const availableCompetitions = useMemo(
    () => getAvailableSoccerCompetitions(gamesData?.games?.soccer),
    [gamesData?.games?.soccer]
  );

  const getVisibleResultCount = (nextFilters: FilterOptions): number => {
    if (!gamesData?.games) {
      return 0;
    }

    return getVisibleGames(filterGames(gamesData.games, nextFilters)).length;
  };

  const trackFilterChanged = (nextFilters: FilterOptions, filterType: 'sport' | 'max' | 'min' | 'competition') => {
    trackAnalyticsEvent(
      'filter_changed',
      buildFilterChangedEvent({
        filterType,
        filters: nextFilters,
        visibleResultCount: getVisibleResultCount(nextFilters),
      }),
      consentSettings.analytics
    );
  };

  useEffect(() => {
    if (sport !== 'soccer' || competition || !gamesData?.games?.soccer || !filters.competitions?.length) {
      return;
    }

    const availableCompetitionSet = new Set(availableCompetitions);
    const validCompetitions = filters.competitions.filter(selectedCompetition =>
      availableCompetitionSet.has(selectedCompetition)
    );

    if (validCompetitions.length !== filters.competitions.length) {
      updateFilters({ competitions: validCompetitions });
    }
  }, [availableCompetitions, competition, filters.competitions, gamesData?.games?.soccer, sport, updateFilters]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const pageStartKey = `${sport}:${location.pathname}`;
    if (pageStartKeyRef.current === pageStartKey) {
      return;
    }

    const entrySource = getEntrySource({
      locationStateEntrySource: (location.state as { entrySource?: AnalyticsEntrySource } | null)?.entrySource,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });

    const tracked = trackAnalyticsEvent('sport_page_start', {
      sport,
      page_path: location.pathname,
      game_count_bucket: getCountBucket(visibleGames.length),
      has_games: visibleGames.length > 0,
      entry_source: entrySource,
      ...getUtmParams(location.search),
    }, consentSettings.analytics);

    if (tracked) {
      pageStartKeyRef.current = pageStartKey;
    }
  }, [
    consentSettings.analytics,
    isLoading,
    location.pathname,
    location.search,
    location.state,
    sport,
    visibleGames.length,
  ]);

  const sportFAQs = getSportFaqs(sport);

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* SEO Components */}
      <MetaGenerator 
        currentSport={getCurrentSport()}
        gameCount={filteredGames.length}
        competitions={getActiveCompetitions()}
        title={sport === 'nba' && !competitionDisplayName ? targetSeoMetadata.sportNba.title : undefined}
        description={sport === 'nba' && !competitionDisplayName ? targetSeoMetadata.sportNba.description : undefined}
      />
      <StructuredDataManager 
        currentSport={getCurrentSport()}
        games={seoGames}
        pageType="sport"
      />
      
      {/* Legacy structured data component (will be replaced by StructuredDataManager) */}
      {/* <SportsStructuredData currentSport={getCurrentSport()} /> */}
      
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <BreadcrumbNav 
          sport={sport}
          competitionName={competitionDisplayName}
        />
        
        <div className="flex items-center mb-4">
          <div className="mr-3">
            <SportEmoji sportType={sport} size="large" className="text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            {competitionDisplayName ? `${competitionDisplayName}` : `${sportDisplayName} Games`}
          </h1>
        </div>
        
        {/* Sport description */}
        <p className="text-gray-400 mb-8 ml-1">{sportDescriptions[sport]}</p>

        <SoccerViewSwitcher
          sport={sport}
          mode="completed"
          competitions={sport === 'soccer' ? getActiveCompetitions() : []}
          className="mb-6"
        />

        <div className="mb-8">
          <PointsFilter 
            value={filters.maxValue}
            setValue={handleMaxValueChange}
            onValueCommit={(value) => {
              trackFilterChanged({ ...filters, maxValue: value }, 'max');
            }}
            sportType={filters.sportType}
            setSportType={(newSport) => {
              const nextFilters: FilterOptions = {
                sportType: newSport,
                maxValue: sportDefaultValues[newSport].default,
                minValue: undefined,
                competitions: []
              };
              trackFilterChanged(nextFilters, 'sport');

              const entrySource = getEntrySource({
                locationStateEntrySource: (location.state as { entrySource?: AnalyticsEntrySource } | null)?.entrySource,
                referrer: typeof document !== 'undefined' ? document.referrer : '',
              });

              // Navigate to the appropriate sport page when sport changes
              navigate(`/sport/${newSport}`, {
                state: { entrySource },
              });
            }}
            minValue={filters.minValue !== undefined ? filters.minValue : getDefaultMinValue(filters.sportType)}
            maxValue={sportLimits[filters.sportType].max}
            defaultValues={{
              'nba': sportDefaultValues.nba.default,
              'soccer': sportDefaultValues.soccer.default,
              'nfl': sportDefaultValues.nfl.default,
              'mlb': sportDefaultValues.mlb.default,
              'nhl': sportDefaultValues.nhl.default
            }}
          >
            {/* Show the competition checkboxes for Soccer */}
            {sport === 'soccer' && (
              <div className="mb-6">
                <CompetitionCheckboxes
                  availableCompetitions={availableCompetitions}
                  selectedCompetitions={filters.competitions || []}
                  onChange={(competitions) => {
                    handleCompetitionsChange(competitions);
                    trackFilterChanged({ ...filters, competitions }, 'competition');
                  }}
                  disabled={!!competition} // Disable if a competition is set in URL
                />
              </div>
            )}
          </PointsFilter>
          
          {/* Show Scores Toggle */}
          <div className="mt-4 flex items-center justify-end">
            <Button 
              onClick={toggleAllScores}
              size="default"
              className={`flex items-center gap-2 font-medium rounded-md ${
                showAllScores 
                  ? `${getToggleColorClass()} text-white` 
                  : 'bg-gray-800/80 hover:bg-gray-700 text-gray-200 border border-gray-700'
              }`}
            >
              {showAllScores ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showAllScores ? 'Hide' : 'Show'} All Scores
            </Button>
          </div>
        </div>

        <GamesList 
          games={filteredGames} 
          isLoading={isLoading} 
          error={error} 
          showAllScores={showAllScores}
          emptyTitle={hasNoSourceGames ? `No ${sportDisplayName} Games Available` : undefined}
          emptyDescription={
            hasNoSourceGames
              ? `${sportDisplayName} has no games in the current data feed.`
              : undefined
          }
          emptyHint={
            hasNoSourceGames
              ? "Check back when new completed games are available."
              : undefined
          }
        />
        
        {/* FAQ Section for better SEO */}
        <FAQSection 
          faqs={sportFAQs}
          className="mt-16 py-16 border-t border-gray-800"
          // Sport routes already ship FAQPage JSON-LD via static route generation.
          injectJsonLd={false}
        />

        {/* Related Sports Navigation */}
        <RelatedSportsNav currentSport={sport} />
        
      </main>
      
      <Footer />
    </div>
  );
};

export default SportPage;
